import React, { useState, useEffect } from 'react';
import { adsAPI } from '../../services/api';

// Module-level cache so all <AdSlot/> instances share one fetch
let _adsCache = null;
let _adsPromise = null;

const fetchAds = () => {
  if (_adsCache) return Promise.resolve(_adsCache);
  if (_adsPromise) return _adsPromise;
  _adsPromise = adsAPI.getActive()
    .then((res) => {
      _adsCache = Array.isArray(res.data) ? res.data : [];
      return _adsCache;
    })
    .catch(() => {
      _adsCache = [];
      return _adsCache;
    });
  return _adsPromise;
};

/**
 * Renders an active ad for a given slot key, or a styled placeholder otherwise.
 * Image is server-side cropped to exact slot dimensions, so layout is stable.
 */
const AdSlot = ({ slotKey, fallbackLabel = 'Reklam Alanı', dims, className = '' }) => {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchAds().then((ads) => {
      if (!alive) return;
      setAd(ads.find((a) => a.key === slotKey) || null);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [slotKey]);

  if (loaded && ad && ad.image_data) {
    const content = (
      <img
        src={ad.image_data}
        alt={ad.label || 'Reklam'}
        className="w-full h-full object-cover block"
        loading="lazy"
        data-testid={`ad-slot-img-${slotKey}`}
      />
    );
    if (ad.target_url) {
      return (
        <a
          href={ad.target_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className={`block overflow-hidden rounded-lg ${className}`}
          data-testid={`ad-slot-link-${slotKey}`}
          style={dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : undefined}
        >
          {content}
        </a>
      );
    }
    return (
      <div
        className={`overflow-hidden rounded-lg ${className}`}
        data-testid={`ad-slot-${slotKey}`}
        style={dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : undefined}
      >
        {content}
      </div>
    );
  }

  // Placeholder (no active ad or still loading)
  return (
    <div
      className={`bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center ${className}`}
      data-testid={`ad-placeholder-${slotKey}`}
      style={dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : undefined}
    >
      <div className="text-center text-gray-400">
        <p className="text-xs font-bold uppercase">{fallbackLabel}</p>
        {dims && <p className="text-xs">{dims.w}x{dims.h}</p>}
      </div>
    </div>
  );
};

export default AdSlot;
